import {
  Box,
  Button,
  Group,
  type GroupProps,
  List,
  Modal,
  Stack,
  Text,
  Title
} from "@mantine/core";
import {
  IconFileInvoice,
  IconGraph,
  IconLayoutGridFilled,
  type TablerIcon
} from "@tabler/icons-react";
import type { ReactNode } from "react";
import { LoadOrError } from "../../../../components/LoadOrError/LoadOrError";
import { ModalFooter } from "../../../../mantine/components/ModalFooter/ModalFooter";
import { backendDateOrDateTimeToLuxonDateTime } from "../../../../utils/dates/backendDateOrDateTimeToLuxonDateTime";
import { DATE_DISPLAY_FORMAT } from "../../../../utils/dates/dates.constants";
import { roundedNumericFormatter } from "../../../../utils/RoundedNumberFormat";
import { useParagraph6CreditGetData } from "../../hooks/useParagraph6CreditGetData";
import type {
  CalculatedGenerationShares,
  Paragraph6EEGCreditData,
  Paragraph6FinancialDocument
} from "../../Paragraph6.types";
import { Paragraph6ParticipationTable } from "./Paragraph6ParticipationTable";

export interface Paragraph6ParticipationModalProps {
  isOpen: boolean;
  onClose: () => void;
  participationCredit: Paragraph6FinancialDocument;
}

function Paragraph6ParticipationModal({
  isOpen,
  onClose,
  participationCredit
}: Paragraph6ParticipationModalProps) {
  const {
    data: participationCreditData,
    isLoading,
    error
  } = useParagraph6CreditGetData(participationCredit.id);

  return (
    <Modal
      className="Paragraph6ParticipationModal"
      opened={isOpen}
      size="min(80vw, 1200px)"
      title={participationCredit?.document_name}
      onClose={onClose}
    >
      <Stack gap="xl">
        <CreditInfo icon={IconGraph} title="Datenverfügbarkeit">
          {participationCreditData?.energy_data_completeness_percentage ===
          null ? (
            <Text c="dimmed" fz="sm">
              Nur für Dokumente verfügbar, die nach dem 10.06.2025 erstellt
              wurden.
            </Text>
          ) : (
            <Text>
              Gutschrift mit{" "}
              {roundedNumericFormatter(
                participationCreditData?.energy_data_completeness_percentage
              )}
              % der verfügbaren Energiedaten erstellt
            </Text>
          )}
        </CreditInfo>

        <CreditInfo icon={IconFileInvoice} title="§ 6 EEG-Vertrag">
          <List>
            <List.Item>
              <Text fw={700} span>
                Gemeinde:
              </Text>{" "}
              {participationCredit?.community_name}
            </List.Item>
            <List.Item>
              <Text fw={700} span>
                Gesellschaft:
              </Text>{" "}
              {participationCredit?.supplier}
            </List.Item>
            <List.Item>
              <Text fw={700} span>
                Leistungszeitraum:
              </Text>{" "}
              {`${participationCreditData?.billing_period_start} - ${participationCreditData?.billing_period_end}`}
            </List.Item>
            <List.Item>
              <Text fw={700} span>
                Erzeugungsanlagen:
              </Text>{" "}
              {participationCreditData?.gemeinde_anteil_erzeuger_data
                .map((generator) => generator.generator_name)
                .join(", ")}
            </List.Item>
          </List>
        </CreditInfo>

        <CreditInfo icon={IconLayoutGridFilled} title="Erzeugungsanteile">
          <GenerationShares participationCreditData={participationCreditData} />
        </CreditInfo>

        <LoadOrError error={error} loading={isLoading}>
          {participationCreditData && (
            <Paragraph6ParticipationTable
              creditData={participationCreditData}
            />
          )}
        </LoadOrError>
      </Stack>
      <ModalFooter>
        <Button variant="default" onClick={onClose}>
          Schließen
        </Button>
      </ModalFooter>
    </Modal>
  );
}

interface ParticipationGeneratorSharesProps {
  participationCreditData?: Paragraph6EEGCreditData;
}

function GenerationShares({
  participationCreditData
}: ParticipationGeneratorSharesProps) {
  const generators = participationCreditData?.gemeinde_anteil_erzeuger_data;

  return (
    <List>
      {generators?.map((generator) => {
        const shares = generator.calculated_generation_shares;
        return (
          <List.Item key={generator.generator_name}>
            <b>{generator.generator_name}</b>:{" "}
            {shares.length === 1 &&
              roundedNumericFormatter(shares[0].share, { decimalScale: 7 })}
            {shares.length === 0 && "-"}
            {shares.length > 1 && <GenerationShareEntry shares={shares} />}
          </List.Item>
        );
      })}
    </List>
  );
}

interface CreditInfoProps extends GroupProps {
  children: ReactNode;
  icon: TablerIcon;
  title: string;
}

function CreditInfo({ children, icon, title, ...groupProps }: CreditInfoProps) {
  const Icon = icon;

  return (
    <Group align="flex-start" gap="md" wrap="nowrap" {...groupProps}>
      <Box mt="xs">
        <Icon color="var(--mantine-color-primary-filled)" />
      </Box>
      <div>
        <Title fw={600} fz="md" mb="sm" order={3}>
          {title}
        </Title>
        {children}
      </div>
    </Group>
  );
}

interface GenerationShareEntryProps {
  shares: CalculatedGenerationShares;
}

function GenerationShareEntry({ shares }: GenerationShareEntryProps) {
  return (
    <List>
      {shares.length > 1 &&
        shares.map((share) => {
          const start = backendDateOrDateTimeToLuxonDateTime(
            share.period_start
          ).toFormat(DATE_DISPLAY_FORMAT);
          const end = backendDateOrDateTimeToLuxonDateTime(
            share.period_end
          ).toFormat(DATE_DISPLAY_FORMAT);
          return (
            <List.Item key={share.period_start}>
              {`${start} - ${end}: ${roundedNumericFormatter(share.share, { decimalScale: 7 })}`}
            </List.Item>
          );
        })}
    </List>
  );
}

export { Paragraph6ParticipationModal };
